import { createStore } from 'vuex';
import axios from 'axios';
import { RouterEnum } from '@/router/router.enums';
import router from '@/router';
import {
  IDLE_LOGOUT_MESSAGE,
  canRefresh,
  isRefreshRejected,
  refreshRequestConfig,
} from '@/utils/consoleRefresh';
import {
  clearTokens,
  endSupportTab,
  getAccessToken,
  getRefreshToken,
  isSupportEnded,
  isSupportTab,
  removeProfile,
  setAccessToken,
  setProfile,
  setRefreshToken,
} from '@/utils/tokenStore';
import {
  createSupportRefresher,
  supportEndedUrl,
} from '@/utils/supportSession';

// 지원 탭 갱신: 쿠키 /auth/refresh 가 아니라 POST /auth/support/refresh(계약 SUPPORT 9), 동시 401 은 한 번만
const refreshSupport = createSupportRefresher({
  post: (path: string, body: unknown) =>
    axios.post(`${process.env.VUE_APP_SERVERURL}${path}`, body),
  store: { getRefreshToken, setAccessToken, setRefreshToken },
});

export interface UserInfo {
  userName?: string;
  companyName?: string;
  companyImageUrl?: string;
}

export interface CompanyInfo {
  [key: string]: unknown;
}

export interface State {
  showSidenav: boolean;
  accessToken: string;
  refreshToken: string;
  userName: string;
  companyName: string;
  companyImageUrl: string;
  isLoading: boolean;
  companyInfo: CompanyInfo;
  messageCount: number;
}

export default createStore<State>({
  state: {
    showSidenav: false,
    accessToken: '',
    refreshToken: '',
    userName: '',
    companyName: '',
    companyImageUrl: '',
    isLoading: false,
    companyInfo: {},
    messageCount: 0,
  },
  mutations: {
    setIsLoading(state, payload: boolean) {
      state.isLoading = payload;
    },
    setCompanyInfo(state, payload: CompanyInfo) {
      state.companyInfo = payload;
    },
    increaseMessageCount(state) {
      state.messageCount++;
    },
    resetMessageCount(state) {
      state.messageCount = 0;
    },
  },
  actions: {
    setUserInfo({ state }, payload: UserInfo) {
      if (payload.userName) state.userName = payload.userName;
      if (payload.companyName) state.companyName = payload.companyName;
      if (payload.companyImageUrl)
        state.companyImageUrl = payload.companyImageUrl;
      setProfile('userNameReseller', payload.userName || '');
      setProfile('companyNameReseller', payload.companyName || '');
      setProfile('companyImageUrlReseller', payload.companyImageUrl || '');
    },

    async getCompanyInfo({ commit }) {
      const accessToken = getAccessToken();
      if (accessToken) {
        try {
          // JWT에서 companyId 추출 (JWT 표준에서 sub 필드에 저장)
          const payload = JSON.parse(atob(accessToken.split('.')[1]));
          const companyId = payload.sub;

          const response = await axios.get(
            `${process.env.VUE_APP_SERVERURL}/companies/${companyId}`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (response.status === 200 || response.status === 201) {
            commit('setCompanyInfo', response.data);
          }
        } catch (error) {
          console.log(error);
        }
      }
    },

    async requestAccessToken({ commit, dispatch, state }) {
      if (isSupportTab()) {
        // 이미 끝난 지원 탭이면 갱신하지 않음(일반 /auth/refresh 로도 넘어가지 않음)
        if (isSupportEnded()) return;
        const result = await refreshSupport();
        if (result.ended) {
          // 갱신을 반복하지 않고 「끝난 지원 탭」으로 묶은 뒤 끝 화면(사유)
          endSupportTab(result.ended);
          window.location.replace(supportEndedUrl(result.ended));
          return;
        }
        state.accessToken = result.accessToken;
        return;
      }
      // 로그아웃한 뒤에는 남은 refresh 쿠키로 세션을 되살리지 않는다
      if (!canRefresh(getAccessToken())) return;
      try {
        // inapi 는 refresh 를 쿠키에서만 읽는다(K-19, CI-07) — Authorization 헤더를 싣지 않음
        const response = await axios.post(
          `${process.env.VUE_APP_SERVERURL}/auth/refresh`,
          null,
          refreshRequestConfig()
        );

        if (response.status === 200 || response.status === 201) {
          state.accessToken = response.data.accessToken;
          state.refreshToken = response.data.refreshToken;
          setAccessToken(response.data.accessToken);
          // 갱신 응답은 access 만 준다(새 refresh 는 쿠키로)
          if (response.data.refreshToken) {
            setRefreshToken(response.data.refreshToken);
          }
        }
      } catch (error) {
        console.log(error);
        commit('setIsLoading', false);

        if (axios.isAxiosError(error) && error.response) {
          if (isRefreshRejected(error)) {
            if (state.messageCount === 0) {
              alert(IDLE_LOGOUT_MESSAGE);
            }
            commit('increaseMessageCount');
            router.push(RouterEnum.Signin.path);
            dispatch('clearLocalStorage');
          }
        }
      }
    },

    // 지원 탭이면 그 탭 지원만 끝(래치), 아니면 localStorage 의 토큰·증표·표시용 키
    clearLocalStorage() {
      if (isSupportTab()) return endSupportTab('ENDED');
      clearTokens();
      removeProfile('userNameReseller');
      removeProfile('companyNameReseller');
      removeProfile('companyImageUrlReseller');
    },
  },
  getters: {
    getMessageCount: (state) => state.messageCount,
    getUserName: (state) => state.userName,
    getCompanyName: (state) => state.companyName,
    getCompanyImageUrl: (state) => state.companyImageUrl,
    getCompanyInfo: (state) => state.companyInfo,
  },
});
